// =============================================================================
// MeuDriver — InstructorFilters — "Mais filtros" (I1: filtros avançados)
// Nome, estado e cidade ficam na barra do topo da busca (InstructorsPage).
// =============================================================================

import { Star, X, ShieldCheck, Tag } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { SPECIALTY_LABELS } from '@/lib/constants'
import { useAllTagDefinitions } from '@/hooks/useInstructorTags'
import { cn } from '@/lib/utils'
import type { InstructorTagDefinitionRow } from '@/types/database'

export interface FilterPanelProps {
  specialties: string[]
  minPrice: string
  maxPrice: string
  minRating: number
  availableDate: string
  womenOnly: boolean
  onlyPro?: boolean
  // P1 — filtros avançados
  minExperienceYears?: number
  onMinExperienceYearsChange?: (v: number) => void
  // U3 — filtro de tags de especialidade
  selectedTagIds?: string[]
  onTagToggle?: (tagId: string) => void
  onSpecialtyToggle: (key: string) => void
  onMinPriceChange: (v: string) => void
  onMaxPriceChange: (v: string) => void
  onMinRatingChange: (v: number) => void
  onAvailableDateChange: (v: string) => void
  onWomenOnlyChange: (v: boolean) => void
  onOnlyProChange?: (v: boolean) => void
  onClear: () => void
}

export function FilterPanel(props: FilterPanelProps) {
  const {
    specialties, minPrice, maxPrice, minRating, availableDate, womenOnly, onlyPro,
    minExperienceYears, onMinExperienceYearsChange,
    selectedTagIds = [], onTagToggle,
    onSpecialtyToggle,
    onMinPriceChange, onMaxPriceChange, onMinRatingChange, onAvailableDateChange,
    onWomenOnlyChange, onOnlyProChange, onClear,
  } = props

  const { data: tagDefinitions = [] } = useAllTagDefinitions()

  // Agrupa tags por categoria para exibição
  const tagsByCategory: Record<string, InstructorTagDefinitionRow[]> = {}
  for (const tag of tagDefinitions) {
    const cat = tag.category ?? 'Outros'
    if (!tagsByCategory[cat]) tagsByCategory[cat] = []
    tagsByCategory[cat].push(tag)
  }

  const hasActiveFilters =
    specialties.length > 0 || minPrice || maxPrice || minRating > 0
    || availableDate || womenOnly || onlyPro || (minExperienceYears ?? 0) > 0
    || selectedTagIds.length > 0

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <Label className="text-sm font-semibold text-gray-700">Especialidades</Label>
        <div className="space-y-2">
          {Object.entries(SPECIALTY_LABELS).map(([key, label]) => (
            <label key={key} className="flex items-center gap-3 cursor-pointer group">
              <div
                className={cn(
                  'h-4 w-4 rounded border-2 flex items-center justify-center transition-all flex-shrink-0',
                  specialties.includes(key)
                    ? 'bg-brand-600 border-brand-600'
                    : 'border-gray-300 group-hover:border-brand-400',
                )}
                onClick={() => onSpecialtyToggle(key)}
              >
                {specialties.includes(key) && (
                  <svg className="h-2.5 w-2.5 text-white" viewBox="0 0 10 10" fill="none">
                    <path d="M2 5l2.5 2.5L8 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </div>
              <span className="text-sm text-gray-600">{label}</span>
            </label>
          ))}
        </div>
      </div>

      <Separator />

      <div className="space-y-3">
        <Label className="text-sm font-semibold text-gray-700">Faixa de preço (R$/hora)</Label>
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <Label className="text-xs text-gray-400">Mínimo</Label>
            <Input type="number" placeholder="0" min={0} value={minPrice} onChange={(e) => onMinPriceChange(e.target.value)} className="h-9" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-gray-400">Máximo</Label>
            <Input type="number" placeholder="500" min={0} value={maxPrice} onChange={(e) => onMaxPriceChange(e.target.value)} className="h-9" />
          </div>
        </div>
      </div>

      <Separator />

      <div className="space-y-3">
        <Label className="text-sm font-semibold text-gray-700">Avaliação mínima</Label>
        <div className="flex items-center gap-1">
          {[1, 2, 3, 4, 5].map((star) => (
            <button key={star} type="button" onClick={() => onMinRatingChange(star === minRating ? 0 : star)} className="focus:outline-none">
              <Star className={cn('h-6 w-6 transition-colors', star <= minRating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300 hover:text-yellow-300')} />
            </button>
          ))}
          {minRating > 0 && <span className="ml-2 text-sm text-gray-500">{minRating}+ estrelas</span>}
        </div>
      </div>

      <Separator />

      <div className="space-y-2">
        <Label className="text-sm font-semibold text-gray-700">Disponível na data</Label>
        <div className="relative">
          <Input
            type="date"
            value={availableDate}
            onChange={(e) => onAvailableDateChange(e.target.value)}
            className="h-10 text-sm"
            min={new Date().toISOString().slice(0, 10)}
          />
          {availableDate && (
            <button type="button" onClick={() => onAvailableDateChange('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <p className="text-xs text-muted-foreground">Mostra instrutores disponíveis neste dia da semana.</p>
      </div>

      <Separator />

      <div className="space-y-2">
        <Label className="text-sm font-semibold text-gray-700">Identidade e destaque</Label>
        <label className="flex items-center gap-3 cursor-pointer group">
          <div
            className={cn(
              'h-4 w-4 rounded border-2 flex items-center justify-center transition-all flex-shrink-0',
              womenOnly
                ? 'bg-pink-500 border-pink-500'
                : 'border-gray-300 group-hover:border-pink-400',
            )}
            onClick={() => onWomenOnlyChange(!womenOnly)}
          >
            {womenOnly && (
              <svg className="h-2.5 w-2.5 text-white" viewBox="0 0 10 10" fill="none">
                <path d="M2 5l2.5 2.5L8 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </div>
          <span className="text-sm text-gray-600">Apenas instrutoras mulheres</span>
        </label>
        <p className="text-xs text-muted-foreground">Filtra instrutoras que se autodeclararam.</p>

        {/* Filtro Pro */}
        {onOnlyProChange && (
          <label className="flex items-center gap-3 cursor-pointer group mt-2">
            <div
              className={cn(
                'h-4 w-4 rounded border-2 flex items-center justify-center transition-all flex-shrink-0',
                onlyPro
                  ? 'bg-brand-600 border-brand-600'
                  : 'border-gray-300 group-hover:border-brand-400',
              )}
              onClick={() => onOnlyProChange(!onlyPro)}
            >
              {onlyPro && (
                <svg className="h-2.5 w-2.5 text-white" viewBox="0 0 10 10" fill="none">
                  <path d="M2 5l2.5 2.5L8 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </div>
            <span className="text-sm text-gray-600 flex items-center gap-1">
              <ShieldCheck className="h-3.5 w-3.5 text-brand-500" />
              Apenas instrutores Pro
            </span>
          </label>
        )}
      </div>

      {/* P1 — Filtro de experiência mínima */}
      {onMinExperienceYearsChange && (
        <>
          <Separator />
          <div className="space-y-2">
            <Label className="text-sm font-semibold text-gray-700">Experiência mínima</Label>
            <div className="flex flex-wrap gap-1.5">
              {[0, 1, 2, 5, 10].map((years) => (
                <button
                  key={years}
                  type="button"
                  onClick={() => onMinExperienceYearsChange(years === minExperienceYears ? 0 : years)}
                  className={cn(
                    'px-2.5 py-1 rounded-full text-xs font-medium border transition-all',
                    (minExperienceYears ?? 0) === years && years > 0
                      ? 'bg-brand-600 text-white border-brand-600'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-brand-300 hover:text-brand-600',
                  )}
                >
                  {years === 0 ? 'Qualquer' : `${years}+ anos`}
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {/* U3 — Filtro de tags de especialidade */}
      {onTagToggle && tagDefinitions.length > 0 && (
        <>
          <Separator />
          <div className="space-y-3">
            <Label className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
              <Tag className="h-3.5 w-3.5" />
              Especialidades do instrutor
            </Label>
            {Object.entries(tagsByCategory).map(([category, tags]) => (
              <div key={category} className="space-y-1.5">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">{category}</p>
                <div className="flex flex-wrap gap-1.5">
                  {tags.map((tag) => {
                    const active = selectedTagIds.includes(tag.id)
                    return (
                      <button
                        key={tag.id}
                        type="button"
                        onClick={() => onTagToggle(tag.id)}
                        className={cn(
                          'px-2.5 py-1 rounded-full text-xs font-medium border transition-all',
                          active
                            ? 'bg-brand-600 text-white border-brand-600'
                            : 'bg-white text-gray-600 border-gray-200 hover:border-brand-300 hover:text-brand-600',
                        )}
                      >
                        {tag.label}
                      </button>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {hasActiveFilters && (
        <Button type="button" variant="ghost" onClick={onClear} className="w-full text-red-500 hover:text-red-600 hover:bg-red-50">
          <X className="h-4 w-4 mr-2" />
          Limpar filtros
        </Button>
      )}
    </div>
  )
}
